/**
 * GENERATED by /docs-writer from docs/transforge.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/transforge.md`.
 *
 * source-sha: 58d0e1da09cd18c64bbc8e6e3337e87c2dd07a63
 * sources (from the markdown's "Sources" section):
 *   - Live production scan, 2026-10-12: kubectl and doctl (read-only) on the two DigitalOcean clusters and the
 *     Oracle Cloud cluster
 *   - TransForge hourly stats rollup (Postgres database transforge), buckets 2026-10-05 to 2026-10-11 and
 *     September 2026
 *   - os-transforge at origin/main b64cf43d: app.ts and assembly-routes.ts (create, upload, status), hmac.ts
 *     (signature), assembly-coordinator.ts, notification-service.ts, maintenance-service.ts, the robots directory
 *     and the production manifests for both clouds
 *   - os-transforge GitLab pipeline 2917158204 (deploy jobs finished 2026-10-08)
 *   - Production MongoDB, database transforge, collection assemblies: bounded read-only queries for 2026-10-05 to
 *     2026-10-11, run on 2026-10-12
 *   - conversation, 2026-10-12
 */
import { Link } from 'react-router';
import { DocScaleBar } from '../components/DocScaleBar';
import { DocSequence } from '../components/DocSequence';
import { DocTopology } from '../components/DocTopology';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'what-transforge-is-made-of', label: 'What TransForge is made of' },
  { id: 'how-an-assembly-runs', label: 'How an assembly runs' },
  { id: 'where-it-runs', label: 'Where it runs' },
  { id: 'how-much-it-processes', label: 'How much it processes' },
  { id: 'how-long-an-assembly-takes', label: 'How long an assembly takes' },
  { id: 'why-assemblies-fail', label: 'Why assemblies fail' },
];

export function TransforgeDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Media · Concept</p>
        <h1 className="doc-title">TransForge</h1>
        <p className="doc-lede">
          TransForge is OptiSigns&rsquo; own media-processing service. A client hands it a file or a URL, and
          TransForge runs it through a <strong>template</strong> of steps, such as import, convert, encode,
          thumbnail and store. One such run is an <strong>assembly</strong>. In production TransForge runs on
          DigitalOcean and on Oracle Cloud, and it created about <strong>96,000 assemblies a day</strong> in the
          week of 5 October 2026. Every figure on this page was read from production on 12 October 2026.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-12">12 Oct 2026</time>
        </p>
        <p className="doc-p">
          Three pages go deeper. <Link to="/docs/transforge-multi-cloud">Multi-cloud</Link> explains the two clouds
          and the global name. <Link to="/docs/transforge-clients">Clients</Link> lists who calls TransForge.{' '}
          <Link to="/docs/transforge-operations">Deploy and operate</Link> covers releases, scaling and the open
          issues.
        </p>

        <h2 className="doc-h2" id="what-transforge-is-made-of">What TransForge is made of</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Part</th>
                <th>Runs on</th>
                <th>What it does</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>API gateway</strong></td>
                <td>Both clouds</td>
                <td>Accepts the create request, checks its signature, receives uploads and answers status reads.</td>
              </tr>
              <tr>
                <td><strong>Assembly service</strong></td>
                <td>Both clouds</td>
                <td>
                  Runs each assembly. It orders the steps, hands each step to a robot and posts the result to the
                  client. It also runs the cleanup, archive and purge jobs.
                </td>
              </tr>
              <tr>
                <td><strong>Robot workers</strong></td>
                <td>Both clouds</td>
                <td>Do the work of one step each. Four worker groups run 14 of the 15 robots.</td>
              </tr>
              <tr>
                <td><strong>File storage</strong></td>
                <td>One share in each cloud</td>
                <td>Holds the working files and results of the assemblies that cloud created.</td>
              </tr>
              <tr>
                <td><strong>Database and queues</strong></td>
                <td>Shared by both clouds</td>
                <td>
                  One MongoDB holds the accounts, keys, templates and assembly records. One Redis holds the job
                  queues.
                </td>
              </tr>
              <tr>
                <td><strong>Console</strong></td>
                <td>DigitalOcean only</td>
                <td>The web console at <code>transforge.optisigns.com</code>, and the API behind it.</td>
              </tr>
              <tr>
                <td><strong>Companion</strong></td>
                <td>DigitalOcean only</td>
                <td>
                  Fetches a file from a customer&rsquo;s Dropbox, Google Drive or OneDrive for the portal upload.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          A <strong>robot</strong> does one kind of step. A template names the robots to run and their order.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Kind of step</th>
                <th>Robots</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Bring the file in</td>
                <td>
                  <code>/upload/handle</code>, <code>/http/import</code>, <code>/file/filter</code>
                </td>
              </tr>
              <tr>
                <td>Images</td>
                <td>
                  <code>/image/resize</code>, <code>/image/optimize</code>
                </td>
              </tr>
              <tr>
                <td>Video</td>
                <td>
                  <code>/video/encode</code>, <code>/video/thumbs</code>, <code>/video/merge</code>
                </td>
              </tr>
              <tr>
                <td>Documents and pages</td>
                <td>
                  <code>/document/convert</code>, <code>/document/thumbs</code>, <code>/html/convert</code>
                </td>
              </tr>
              <tr>
                <td>Store the output</td>
                <td>
                  <code>/s3/store</code>, <code>/digitalocean/store</code>, <code>/cloudflare/storage</code>,{' '}
                  <code>/oracle/storage</code>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          Three robots have a worker group of their own, because their load differs from the rest:{' '}
          <code>/video/encode</code>, <code>/document/thumbs</code> and <code>/html/convert</code>. One general
          group runs the others.
        </p>

        <h2 className="doc-h2" id="how-an-assembly-runs">How an assembly runs</h2>
        <DocSequence
          caption="One assembly with an uploaded file, from create to result"
          participants={[
            { id: 'Client', label: 'Client', note: 'a job, the portal or a device' },
            { id: 'Api', label: 'API gateway' },
            { id: 'Asm', label: 'Assembly service' },
            { id: 'Rob', label: 'Robot workers' },
            { id: 'Store', label: 'Object storage', note: 'Spaces, S3 or R2' },
            { id: 'Cb', label: 'upload-processor', note: "the client's callback" },
          ]}
          steps={[
            { from: 'Client', to: 'Api', text: 'Create the assembly, signed' },
            { kind: 'reply', from: 'Api', to: 'Client', text: 'Return status URL and upload URL' },
            { from: 'Client', to: 'Api', text: 'Upload the file in chunks' },
            {
              kind: 'note',
              over: ['Api', 'Asm'],
              text: 'Processing starts when every expected file has arrived',
            },
            { from: 'Asm', to: 'Rob', text: 'Queue each step' },
            { from: 'Rob', to: 'Store', text: 'Write the output files' },
            { kind: 'reply', from: 'Rob', to: 'Asm', text: 'Report each step result' },
            { from: 'Asm', to: 'Cb', text: 'Post the final result' },
            { from: 'Client', to: 'Api', text: 'Read the status' },
            { kind: 'reply', from: 'Api', to: 'Client', text: 'Return status and results' },
          ]}
        />
        <ul className="doc-list">
          <li>
            <strong>The create request is signed.</strong> Each account has a key and a secret. The client signs the
            request parameters with the secret, and the API gateway rejects a signature that does not match.
          </li>
          <li>
            <strong>A file arrives in one of three ways.</strong> The client uploads it in resumable chunks, or
            sends it inside the create request, or names a URL that TransForge fetches itself. An assembly that
            expects no upload starts at once.
          </li>
          <li>
            <strong>The returned URLs name one cloud.</strong> The status URL and the upload URL in the create
            response point at the cloud that created the assembly. Every later request for that assembly must use
            them.
          </li>
          <li>
            <strong>The callback is optional.</strong> A client that sets no callback address reads the status
            until the assembly ends. The assembly service retries a callback that fails.
          </li>
        </ul>

        <h2 className="doc-h2" id="where-it-runs">Where it runs</h2>
        <p className="doc-p">
          TransForge runs as two copies. A request goes from a client to one cloud&rsquo;s API gateway and stays in
          that cloud until its output is stored. The two clouds share one database and one queue server.
        </p>
        <DocTopology
          caption="TransForge in production, 12 October 2026"
          groups={[
            { id: 'Do', label: 'DigitalOcean', note: 'its own file share' },
            { id: 'Oci', label: 'Oracle Cloud', note: 'its own file share' },
          ]}
          nodes={[
            { id: 'Direct', label: 'Ten clients', note: 'jobs, uploads' },
            { id: 'Giphy', label: 'Giphy', note: 'one client' },
            { id: 'Global', label: 'Global name', note: 'DNS steering' },
            { id: 'DoApi', label: 'API gateway', note: '8 pods', group: 'Do' },
            { id: 'DoAsm', label: 'Assembly service', note: '4 pods', group: 'Do' },
            { id: 'DoRob', label: 'Robot workers', note: 'autoscaled', group: 'Do' },
            { id: 'OciApi', label: 'API gateway', note: '2 pods', group: 'Oci' },
            { id: 'OciAsm', label: 'Assembly service', note: '2 pods', group: 'Oci' },
            { id: 'OciRob', label: 'Robot workers', note: 'fixed size', group: 'Oci' },
            { id: 'Shared', label: 'MongoDB, Redis', note: 'shared', kind: 'store' },
            { id: 'Store', label: 'Object storage', note: 'output files', kind: 'store' },
          ]}
          edges={[
            { from: 'Direct', to: 'DoApi' },
            { from: 'Giphy', to: 'Global' },
            { from: 'Global', to: 'DoApi', text: '90' },
            { from: 'Global', to: 'OciApi', text: '10' },
            { from: 'DoApi', to: 'DoAsm' },
            { from: 'DoAsm', to: 'DoRob' },
            { from: 'OciApi', to: 'OciAsm' },
            { from: 'OciAsm', to: 'OciRob' },
            { from: 'DoRob', to: 'Store' },
            { from: 'OciRob', to: 'Store' },
            { from: 'Do', to: 'Shared', dashed: true },
            { from: 'Oci', to: 'Shared', dashed: true },
          ]}
        />
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Cloud</th>
                <th>Cluster</th>
                <th className="doc-num">Nodes</th>
                <th className="doc-num">Pods</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>DigitalOcean, New York</td>
                <td><code>k8s-optisigns-prod-transforge-api</code></td>
                <td className="doc-num">4</td>
                <td className="doc-num">11</td>
              </tr>
              <tr>
                <td>DigitalOcean, New York</td>
                <td><code>k8s-optisigns-prod-transforge-worker</code></td>
                <td className="doc-num">12</td>
                <td className="doc-num">22</td>
              </tr>
              <tr>
                <td>Oracle Cloud, Ashburn</td>
                <td><code>transforge-prod</code></td>
                <td className="doc-num">5</td>
                <td className="doc-num">11</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The pod count is the running TransForge pods and leaves out system pods. On DigitalOcean the API gateway,
          the console API and Companion run in the first cluster, and the assembly service and the robot workers run
          in the second. On Oracle Cloud one cluster holds everything, on ARM nodes.
        </p>
        <p className="doc-p">
          Both clouds run release <code>b64cf43d</code>, deployed on 8 October 2026.{' '}
          <Link to="/docs/transforge-operations">Deploy and operate</Link> describes how a release gets there.
          DigitalOcean created every assembly in the week below.{' '}
          <Link to="/docs/transforge-multi-cloud">Multi-cloud</Link> explains why Oracle Cloud created none.
        </p>

        <h2 className="doc-h2" id="how-much-it-processes">How much it processes</h2>
        <p className="doc-p">
          TransForge created <strong>671,527 assemblies</strong> in the seven days from 5 to 11 October 2026, and
          98.38% of them completed.
        </p>
        <DocScaleBar
          caption="Assemblies by client, 5 to 11 October 2026"
          total="671,527"
          items={[
            { label: 'Google Drive job', value: 313843 },
            { label: 'Google Slides job', value: 157130 },
            { label: 'Portal web upload', value: 90274 },
            { label: 'Screenshot-website job', value: 50357 },
            { label: 'Canvas', value: 44909 },
            { label: 'Five other clients', value: 15014 },
          ]}
        />
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th className="doc-num">Assemblies</th>
                <th className="doc-num">Share</th>
                <th className="doc-num">Failed</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google Drive job</td>
                <td className="doc-num">313,843</td>
                <td className="doc-num">46.7%</td>
                <td className="doc-num">1.03%</td>
              </tr>
              <tr>
                <td>Google Slides job</td>
                <td className="doc-num">157,130</td>
                <td className="doc-num">23.4%</td>
                <td className="doc-num">0.75%</td>
              </tr>
              <tr>
                <td>Portal web upload</td>
                <td className="doc-num">90,274</td>
                <td className="doc-num">13.4%</td>
                <td className="doc-num">0.15%</td>
              </tr>
              <tr>
                <td>Screenshot-website job</td>
                <td className="doc-num">50,357</td>
                <td className="doc-num">7.5%</td>
                <td className="doc-num">9.88%</td>
              </tr>
              <tr>
                <td>Canvas</td>
                <td className="doc-num">44,909</td>
                <td className="doc-num">6.7%</td>
                <td className="doc-num">0.03%</td>
              </tr>
              <tr>
                <td>Five other clients</td>
                <td className="doc-num">15,014</td>
                <td className="doc-num">2.2%</td>
                <td className="doc-num">4.27%</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The screenshot job fails far more often than any other client: 4,975 of its 50,357 assemblies failed.{' '}
          <Link to="/docs/transforge-clients">Clients</Link> lists every client.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Measure</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Assemblies a day</td>
                <td>About 95,900. Weekdays run 99,000 to 105,000, and weekends 79,000 to 83,000.</td>
              </tr>
              <tr>
                <td>Busiest hour</td>
                <td>6,282 assemblies, on 5 October 2026 at 14:00 UTC</td>
              </tr>
              <tr>
                <td>Steps in an assembly</td>
                <td>10.6 on average</td>
              </tr>
              <tr>
                <td>Wait in a queue</td>
                <td>0.9 seconds on average. Video encoding waits 93 seconds on average.</td>
              </tr>
              <tr>
                <td>Data in seven days</td>
                <td>6.79 TB processed and 4.28 TB stored</td>
              </tr>
              <tr>
                <td>September 2026</td>
                <td>2,790,347 assemblies, 98.53% completed, 25.96 TB processed</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 className="doc-h2" id="how-long-an-assembly-takes">How long an assembly takes</h2>
        <p className="doc-p">
          Half of all assemblies finish within 3.8 seconds of the create request, and 95% within 37.6 seconds. The
          times below are for assemblies that completed from 5 to 11 October 2026.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th className="doc-num">Half finish within</th>
                <th className="doc-num">95% within</th>
                <th className="doc-num">99% within</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>All clients</td>
                <td className="doc-num">3.8 s</td>
                <td className="doc-num">37.6 s</td>
                <td className="doc-num">121.6 s</td>
              </tr>
              <tr>
                <td>Google Sheets</td>
                <td className="doc-num">3.6 s</td>
                <td className="doc-num">14.3 s</td>
                <td className="doc-num">61.0 s</td>
              </tr>
              <tr>
                <td>Google Slides</td>
                <td className="doc-num">6.8 s</td>
                <td className="doc-num">61.0 s</td>
                <td className="doc-num">139.6 s</td>
              </tr>
              <tr>
                <td>Portal web upload</td>
                <td className="doc-num">4.8 s</td>
                <td className="doc-num">62.4 s</td>
                <td className="doc-num">254.1 s</td>
              </tr>
              <tr>
                <td>Screenshot-website job</td>
                <td className="doc-num">6.5 s</td>
                <td className="doc-num">47.3 s</td>
                <td className="doc-num">201.8 s</td>
              </tr>
              <tr>
                <td>Canvas, copy files</td>
                <td className="doc-num">1.2 s</td>
                <td className="doc-num">2.1 s</td>
                <td className="doc-num">3.4 s</td>
              </tr>
              <tr>
                <td>OneDrive Excel</td>
                <td className="doc-num">5.6 s</td>
                <td className="doc-num">58.2 s</td>
                <td className="doc-num">164.1 s</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          The portal time includes the upload from the customer&rsquo;s browser. Without the upload, 95% of portal
          assemblies finish within 14.0 seconds.
        </p>

        <h2 className="doc-h2" id="why-assemblies-fail">Why assemblies fail</h2>
        <p className="doc-p">
          10,242 assemblies failed in those seven days. Eight causes explain 91% of them, and most start outside
          TransForge: a source that refuses the request, or a page that cannot be loaded.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Cause</th>
                <th className="doc-num">Failed</th>
                <th>Client</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google refuses the download with &quot;too many requests&quot;</td>
                <td className="doc-num">2,920</td>
                <td>Google Sheets</td>
              </tr>
              <tr>
                <td>The page&rsquo;s host name does not resolve</td>
                <td className="doc-num">2,011</td>
                <td>Screenshot</td>
              </tr>
              <tr>
                <td>The page takes more than 60 seconds to load</td>
                <td className="doc-num">1,497</td>
                <td>Screenshot</td>
              </tr>
              <tr>
                <td>The exported file has no page to make an image from</td>
                <td className="doc-num">1,023</td>
                <td>Google Slides</td>
              </tr>
              <tr>
                <td>Microsoft refuses the download with HTTP 406</td>
                <td className="doc-num">624</td>
                <td>OneDrive Excel</td>
              </tr>
              <tr>
                <td>The address given is not a URL</td>
                <td className="doc-num">573</td>
                <td>Screenshot</td>
              </tr>
              <tr>
                <td>A signature check fails while storing the output to S3</td>
                <td className="doc-num">339</td>
                <td>Sheets, portal, Slides</td>
              </tr>
              <tr>
                <td>The network changes while the page loads</td>
                <td className="doc-num">308</td>
                <td>Screenshot</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">The S3 failure happened on one day only: all 339 are from 5 October 2026.</p>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Running TransForge?</h2>
            <p>Releases, scaling limits, retention and the issues open today are on one page.</p>
          </div>
          <Link className="btn" to="/docs/transforge-operations">
            Read Deploy and operate
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
